import cn from 'classnames';
import { FC, ReactNode, type ComponentPropsWithRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { Description } from '../Description/Description';
import { Modal } from '../Modal/Modal';

import styles from './MedicalImageZoomLayout.module.scss';

type MedicalImageZoomLayoutProps = {
  isOpen: boolean;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  contentProps?: ComponentPropsWithRef<'div'>;
  controls?: ReactNode;
  imageContainerClassName?: string;
  order?: number;
  strideMm?: number;
  thicknessControl?: ReactNode;
  thicknessMm?: number | string;
  title?: string;
  onClose: () => void;
  renderToolbar?: (toolbarClassName: string) => ReactNode;
};

export const MedicalImageZoomLayout: FC<MedicalImageZoomLayoutProps> = (
  props,
) => {
  const {
    isOpen,
    children,
    actions,
    className,
    contentProps,
    controls,
    imageContainerClassName,
    order,
    strideMm,
    thicknessControl,
    thicknessMm,
    title,
    onClose,
    renderToolbar,
  } = props;

  return (
    <Modal
      className={styles.modalRoot}
      data-component="medical-image-zoom-dialog"
      isOpen={isOpen}
      onCancel={onClose}
      title={title}
      containerClassName={cn(styles.modalContainer, className)}
      bodyClassName={styles.modalBody}
      hideFooter
    >
      {!!strideMm && (
        <Description
          fontType="p2"
          hideColon={!!thicknessControl}
          label={
            <FormattedMessage
              id="report.slice_step"
              defaultMessage="Slice step"
            />
          }
          className={cn(
            styles.thicknessAndStride,
            !!thicknessControl && styles.controlDescription,
          )}
          contentClassName={cn(!!thicknessControl && styles.controlValue)}
        >
          {strideMm} <FormattedMessage id="global.mm" defaultMessage="mm" />
        </Description>
      )}
      {thicknessControl ? (
        <div className={styles.thicknessControl}>{thicknessControl}</div>
      ) : (
        !!thicknessMm && (
          <Description
            fontType="p2"
            label={
              <FormattedMessage
                id="report.sliceThickness"
                defaultMessage="slice thickness"
              />
            }
            className={styles.thicknessAndStride}
          >
            {thicknessMm}{' '}
            <FormattedMessage id="global.mm" defaultMessage="mm" />
          </Description>
        )
      )}
      <div
        {...contentProps}
        className={cn(styles.content, contentProps?.className)}
      >
        {!!controls && <div className={styles.controls}>{controls}</div>}
        <div className={styles.imageWrapper}>
          <div className={cn(styles.imageContainer, imageContainerClassName)}>
            {!!order && (
              <span className={cn(styles.orderText, 'p1')}>{order}</span>
            )}
            {children}
          </div>

          {renderToolbar?.(styles.toolbar)}
        </div>

        {!!actions && <div className={styles.actions}>{actions}</div>}
      </div>
    </Modal>
  );
};
